import type { Course, CourseVariant, ElevatedArea, ElevationRamp, Point } from "./engine";

// One world layout for simulation and rendering. Fairways leave room to choose
// a safer approach or carry a ravine onto an upper terrace.
const p = (x: number, z: number): Point => ({ x: x + 180, y: z + 280 });
const polygon = (vertices: number[][]) => vertices.map(([x, z]) => p(x, z));
const rect = (x: number, z: number, w: number, d: number) =>
  polygon([
    [x, z],
    [x + w, z],
    [x + w, z + d],
    [x, z + d],
  ]);
const terrace = (x: number, z: number, w: number, d: number, height: number): ElevatedArea => ({
  points: rect(x, z, w, d),
  height,
});
const ramp = (
  x: number,
  z: number,
  ex: number,
  ez: number,
  width: number,
  startHeight: number,
  endHeight: number,
  launch = false,
): ElevationRamp => ({ start: p(x, z), end: p(ex, ez), width, startHeight, endHeight, launch });
const sand = (x: number, z: number, rx: number, ry: number) => ({ ...p(x, z), rx, ry });
const variants = (
  id: string,
  hint: string,
  extraSand = sand(330, 600, 65, 100),
): CourseVariant[] => [
  { id: `${id}-open`, hint, obstacles: [], sand: [], boosts: [] },
  { id: `${id}-bunker`, hint, obstacles: [], sand: [extraSand], boosts: [] },
];
const wide = polygon([
  [-470, -1120],
  [400, -1120],
  [520, -780],
  [430, -260],
  [520, 300],
  [460, 1060],
  [-460, 1060],
  [-530, 520],
  [-450, 80],
  [-540, -600],
]);
const bend = polygon([
  [-670, -1180],
  [260, -1180],
  [460, -900],
  [340, -540],
  [520, -80],
  [460, 540],
  [360, 1080],
  [-410, 1080],
  [-500, 670],
  [-260, 120],
  [-220, -300],
  [-580, -490],
  [-770, -820],
]);
const mirror = (points: Point[]) => points.map(({ x, y }) => ({ x: 360 - x, y }));

/** Broad, winding fairways with mitered corners instead of reusing one outline. */
function ribbon(points: Point[], width: number): Point[] {
  const normal = (a: Point, b: Point) => {
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    return { x: -(b.y - a.y) / length, y: (b.x - a.x) / length };
  };
  const left: Point[] = [],
    right: Point[] = [];
  for (let i = 0; i < points.length; i++) {
    const before = normal(points[Math.max(0, i - 1)], points[i === 0 ? 1 : i]);
    const after = normal(
      points[i === points.length - 1 ? i - 1 : i],
      points[Math.min(points.length - 1, i + 1)],
    );
    const length = Math.hypot(before.x + after.x, before.y + after.y);
    const nx = (before.x + after.x) / length,
      ny = (before.y + after.y) / length;
    const offset = width / 2 / Math.max(0.55, nx * after.x + ny * after.y);
    left.push({ x: points[i].x + nx * offset, y: points[i].y + ny * offset });
    right.push({ x: points[i].x - nx * offset, y: points[i].y - ny * offset });
  }
  return [...left, ...right.reverse()];
}
const coastRoute = [p(0, 900), p(0, -600), p(1000, -1600), p(-700, -2800), p(-700, -3200)];
const coastBoundary = ribbon(
  [p(0, 1100), p(0, -700), p(1000, -1600), p(-700, -2800), p(-700, -3480)],
  900,
);
const summitRoute = [
  p(-650, 2100),
  p(800, 950),
  p(-600, -750),
  p(800, -2400),
  p(-600, -4050),
  p(-600, -5700),
];
const summitBoundary = ribbon([p(-650, 2450), ...summitRoute.slice(0, -1), p(-600, -6030)], 900);

export const courses: Course[] = [
  {
    name: "Kiefernwiese",
    par: 3,
    difficulty: "Leicht",
    theme: "grove",
    turf: "#76b841",
    boundary: polygon([
      [-380, -820],
      [260, -860],
      [440, -440],
      [320, 20],
      [440, 490],
      [350, 1010],
      [-350, 1010],
      [-450, 570],
      [-330, 80],
      [-480, -330],
    ]),
    start: p(0, 820),
    cup: p(-100, -660),
    hint: "Ein ruhiger Auftakt: viel Platz und ein sanfter Anstieg zum Grün.",
    gaps: [],
    elevatedAreas: [terrace(-260, -780, 320, 240, 35)],
    elevationRamps: [ramp(-100, -240, -100, -540, 320, 0, 35)],
    obstacles: [],
    boosts: [],
    sand: [sand(200, 170, 82, 135)],
    variants: variants(
      "meadow",
      "Die linke Seite bleibt frei. Ein langer Schlag bringt dich bis vor das leicht erhöhte Grün.",
      sand(-270, 400, 65, 100),
    ),
  },
  {
    name: "Mühlenklamm",
    par: 4,
    difficulty: "Mittel",
    theme: "grove",
    turf: "#76b841",
    boundary: wide,
    start: p(80, 900),
    cup: p(-110, -940),
    hint: "Vom Hochplateau über den Mühlenbach und hinauf zum Turmgrün.",
    gaps: [rect(-425, -170, 850, 300)],
    elevatedAreas: [
      terrace(-270, 680, 560, 330, 100),
      terrace(-270, -570, 540, 400, 55),
      terrace(-310, -1070, 410, 270, 185),
    ],
    elevationRamps: [
      ramp(80, 680, 80, 410, 260, 100, 0),
      ramp(80, 410, 80, 130, 260, 0, 105, true),
      ramp(-110, -570, -110, -800, 280, 55, 185),
    ],
    obstacles: [],
    boosts: [],
    sand: [sand(240, -680, 80, 95)],
    variants: variants(
      "mill-carry",
      "Nutze das Gefälle für Tempo. Die breite Schanze endet am Bach – halte auf die gegenüberliegende Terrasse.",
      sand(-350, 520, 55, 110),
    ),
  },
  {
    name: "Dünenpass",
    par: 4,
    difficulty: "Mittel",
    theme: "dunes",
    turf: "#76b841",
    boundary: bend,
    start: p(0, 920),
    cup: p(-510, -960),
    hint: "Zwei versetzte Sprünge und ein hoch gelegenes Grün hinter dem Linksbogen.",
    gaps: [rect(-180, 40, 570, 240), rect(-580, -700, 700, 190)],
    elevatedAreas: [terrace(-160, -280, 470, 320, 80), terrace(-650, -1090, 670, 390, 120)],
    elevationRamps: [
      ramp(90, 590, 90, 280, 270, 0, 115, true),
      ramp(20, -280, -160, -510, 235, 80, 180, true),
    ],
    obstacles: [],
    boosts: [],
    bumpers: [{ ...p(380, 380), radius: 26, kick: 54 }],
    sand: [sand(-340, 520, 65, 100)],
    variants: variants(
      "eagle-dogleg",
      "Erst auf die mittlere Terrasse. Richte dich dort links zur zweiten Schanze aus.",
      sand(280, 720, 65, 90),
    ),
  },
  {
    name: "Küstenviadukt",
    par: 3,
    difficulty: "Leicht",
    openEdges: true,
    theme: "coast",
    turf: "#76b841",
    boundary: mirror(wide),
    start: p(0, 900),
    cup: p(110, -950),
    hint: "Ein schmales Viadukt oder der weite Sprung auf die sonnige obere Ebene.",
    gaps: [rect(-360, -340, 760, 500)],
    elevatedAreas: [
      { ...terrace(-315, -340, 140, 500, 90), bridge: true },
      terrace(-315, -680, 610, 340, 90),
      terrace(-100, -1080, 400, 230, 120),
    ],
    elevationRamps: [
      ramp(-245, 580, -245, 160, 140, 0, 90),
      ramp(80, 490, 80, 160, 260, 0, 155, true),
      ramp(110, -680, 110, -850, 280, 90, 120),
    ],
    obstacles: [],
    boosts: [],
    sand: [sand(280, 540, 85, 140)],
    variants: variants(
      "sun-viaduct",
      "Links führt die schmale Brücke über den Abgrund. Die Schanze rechts bietet eine direkte Fluglinie.",
      sand(-340, 780, 70, 90),
    ),
  },
  {
    name: "Felsenkaskade",
    baseIsHazard: true,
    par: 5,
    difficulty: "Schwer",
    openEdges: true,
    theme: "twilight",
    turf: "#76b841",
    boundary: mirror(bend),
    start: p(0, 920),
    cup: p(520, -970),
    hint: "Drei Höhenstufen zwischen Felsen, mit einer Schanze über den Querbalken.",
    gaps: [rect(-190, -710, 740, 190)],
    elevatedAreas: [
      terrace(-230, 470, 500, 550, 145),
      terrace(-260, -230, 460, 400, 55),
      terrace(-30, -1100, 690, 390, 105),
    ],
    elevationRamps: [
      ramp(0, 470, 0, 170, 260, 145, 55),
      ramp(0, -230, 140, -520, 260, 55, 175, true),
    ],
    obstacles: [rect(-180, -40, 360, 26)],
    boosts: [],
    sand: [],
    variants: variants(
      "rock-cascade",
      "Vom hohen Abschlag bergab, am Querbalken vorbei und über die schräge Schanze zur letzten Terrasse.",
      sand(180, 680, 45, 90),
    ),
  },
  {
    name: "Oasenstufen",
    par: 4,
    difficulty: "Mittel",
    theme: "dunes",
    turf: "#76b841",
    boundary: wide,
    start: p(0, 900),
    cup: p(-70, -950),
    hint: "Von Ebene zu Ebene bis zum höchsten Grün des Waldes.",
    gaps: [rect(-350, -280, 700, 180)],
    elevatedAreas: [
      terrace(-320, 210, 580, 210, 80),
      terrace(-170, -510, 470, 230, 155),
      terrace(-300, -1080, 480, 290, 270),
    ],
    elevationRamps: [
      ramp(-90, 760, -90, 420, 270, 0, 80),
      ramp(60, 210, 60, -100, 280, 80, 220, true),
      ramp(-70, -510, -70, -790, 280, 155, 270),
    ],
    obstacles: [],
    boosts: [],
    sand: [sand(340, 320, 62, 125)],
    variants: variants(
      "sky-stairs",
      "Drei Terrassen: vorlegen, über die Lücke springen, dann den letzten Anstieg nehmen.",
      sand(-370, -730, 55, 85),
    ),
  },
  {
    name: "Klippenreise",
    par: 6,
    difficulty: "Schwer",
    openEdges: true,
    route: coastRoute,
    theme: "coast",
    turf: "#76b841",
    boundary: coastBoundary,
    start: p(0, 900),
    cup: p(-700, -3200),
    hint: "Über die Bucht, um zwei Landzungen und erst dann zum Leuchtturmgrün.",
    gaps: [rect(-420, -370, 840, 660)],
    elevatedAreas: [terrace(-340, -760, 680, 390, 65), terrace(-1030, -3350, 660, 250, 95)],
    elevationRamps: [
      ramp(0, 600, 0, 290, 310, 0, 175, true),
      ramp(60, -760, 400, -1060, 400, 65, 0),
      ramp(-700, -2800, -700, -3100, 600, 0, 95),
    ],
    obstacles: [],
    boosts: [{ ...p(0, 470), angle: -Math.PI / 2, radius: 52, speed: 620 }],
    sand: [sand(-370, 710, 55, 130), sand(0, -600, 185, 100)],
    variants: variants(
      "wind-flight",
      "Erst über die Bucht springen, dann den goldenen Etappenmarkierungen um die Landzungen folgen. Das Loch wartet hinter dem zweiten Bogen.",
      sand(370, 790, 50, 90),
    ),
  },
  {
    name: "Gipfelexpedition",
    par: 7,
    difficulty: "Sehr schwer",
    theme: "twilight",
    turf: "#76b841",
    boundary: summitBoundary,
    openEdges: true,
    route: summitRoute,
    start: summitRoute[0],
    cup: summitRoute[summitRoute.length - 1],
    hint: "Eine lange Bergtour über fünf Etappen. Hier braucht es mehrere volle Schläge bis zum Grün.",
    elevatedAreas: [{ points: summitBoundary, height: 140 }, terrace(-1000, -5940, 800, 630, 320)],
    elevationRamps: [ramp(-600, -4820, -600, -5310, 650, 140, 320)],
    gaps: [],
    obstacles: [],
    boosts: [],
    sand: [sand(800, 950, 140, 150), sand(-600, -750, 130, 160), sand(800, -2400, 150, 140)],
    variants: variants(
      "summit-expedition",
      "Fünf Etappen bis zur Fahne: lange Abschläge, Richtungswechsel und ein letzter Anstieg. Die goldenen Markierungen zeigen den Weg.",
      sand(-600, -4050, 100, 140),
    ),
  },
];
